import { useCallback, useEffect, useRef } from 'react';
import { useDataStore } from '@/store/dataStore';
import { runPipeline } from '@/data/pipeline';

/**
 * Loads (and reloads) the full market dataset. Cancels any in-flight fetch
 * before starting a new one so a rapid double-refresh can't race and leave
 * stale data displayed as current.
 */
export function useMarketData() {
  const setLoading = useDataStore((s) => s.setLoading);
  const setLoaded = useDataStore((s) => s.setLoaded);
  const setError = useDataStore((s) => s.setError);
  const status = useDataStore((s) => s.status);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading();
    try {
      const result = await runPipeline(controller.signal);
      if (controller.signal.aborted) return;
      setLoaded(result);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError((err as Error).message ?? 'Unknown error loading market data.');
    }
  }, [setLoading, setLoaded, setError]);

  useEffect(() => {
    refresh();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, refresh };
}
